import {
  type CSSProperties,
  type ReactNode,
  useInsertionEffect,
  useLayoutEffect,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import {
  IconType,
  type IconDefinition,
  type MaterialFontIconDefinition,
  type SvgIconComponentType,
} from "./IconType";
import materialSymbolsRoundedSubsetUrl from "./assets/material-symbols-rounded-subset.woff2?url";

const MATERIAL_SYMBOLS_SCALE = 1.125;
const MATERIAL_SYMBOLS_MEASUREMENT_FONT_SIZE_PX = 24;
const MATERIAL_SYMBOLS_BASE_CLASSNAME =
  "inline-flex shrink-0 select-none items-center justify-center";
const MATERIAL_SYMBOLS_GLYPH_CLASSNAME =
  "flex h-full w-full items-center justify-center whitespace-nowrap font-normal not-italic leading-none normal-case";
const materialGlyphSupportCache = new Map<string, boolean>();
const DEFAULT_STYLE: CSSProperties = {};

export interface IIcon {
  icon: IconType;
  size?: number | string;
  color?: string;
  horizontal?: boolean;
  vertical?: boolean;
  rotate?: number;
  style?: CSSProperties;
  className?: string;
  viewBox?: string;
  onClick?: () => void;
  title?: string | null;
  description?: string | null;
  children?: ReactNode;
}

export const Icon = ({
  icon,
  size = 0.85,
  color = "currentColor",
  horizontal = false,
  vertical = false,
  rotate = 0,
  style = DEFAULT_STYLE,
  className,
  viewBox: manualViewBox,
  onClick,
  title = null,
  description = null,
  ...props
}: IIcon) => {
  const iconDefinition = IconType[icon];
  const materialIconDefinition =
    iconDefinition && isMaterialFontIconDefinition(iconDefinition)
      ? iconDefinition
      : null;

  useInsertionEffect(() => {
    if (!materialIconDefinition) return;
    ensureMaterialSymbolsFontFace();
  }, [materialIconDefinition]);

  const materialGlyphSupported = useMaterialGlyphSupport(
    materialIconDefinition,
  );

  if (!iconDefinition) {
    if (import.meta.env.DEV) {
      console.error(`Icon component for "${icon}" is undefined.`);
    }
    return null;
  }

  const computedSize = getComputedSize(size);
  const sharedStyle = getSharedStyle({
    computedSize,
    horizontal,
    rotate,
    vertical,
  });
  const accessibilityProps = getAccessibilityProps({ description, title });

  if (isMaterialFontIconDefinition(iconDefinition)) {
    if (!materialGlyphSupported) return null;

    return renderMaterialIcon({
      accessibilityProps,
      className,
      color,
      computedSize,
      iconDefinition,
      onClick,
      props,
      sharedStyle,
      style,
    });
  }

  return renderSvgIcon({
    accessibilityProps,
    className,
    color,
    description,
    iconDefinition,
    manualViewBox,
    onClick,
    props,
    sharedStyle,
    style,
  });
};

const getComputedSize = (
  size: NonNullable<IIcon["size"]>,
): NonNullable<IIcon["size"]> =>
  typeof size === "number" ? `${size * 1.5}rem` : size;

const getSharedStyle = ({
  computedSize,
  horizontal,
  rotate,
  vertical,
}: {
  computedSize: NonNullable<IIcon["size"]>;
  horizontal: boolean;
  rotate: number;
  vertical: boolean;
}): CSSProperties => {
  const transform: string[] = [];
  if (horizontal) transform.push("scaleX(-1)");
  if (vertical) transform.push("scaleY(-1)");
  if (rotate !== 0) transform.push(`rotate(${rotate}deg)`);

  return {
    width: computedSize,
    height: computedSize,
    transform: transform.length > 0 ? transform.join(" ") : undefined,
    transformOrigin: transform.length > 0 ? "center" : undefined,
  };
};

const getAccessibilityProps = ({
  description,
  title,
}: Pick<IIcon, "description" | "title">) =>
  title
    ? {
        role: "img" as const,
        "aria-label": title,
        "aria-description": description ?? undefined,
        title,
      }
    : { "aria-hidden": true as const };

type AccessibilityProps = ReturnType<typeof getAccessibilityProps>;
type IconChildrenProps = Pick<IIcon, "children">;

const renderMaterialIcon = ({
  accessibilityProps,
  className,
  color,
  computedSize,
  iconDefinition,
  onClick,
  props,
  sharedStyle,
  style,
}: {
  accessibilityProps: AccessibilityProps;
  className: IIcon["className"];
  color: NonNullable<IIcon["color"]>;
  computedSize: NonNullable<IIcon["size"]>;
  iconDefinition: MaterialFontIconDefinition;
  onClick: IIcon["onClick"];
  props: IconChildrenProps;
  sharedStyle: CSSProperties;
  style: CSSProperties;
}) => {
  const containerStyle: CSSProperties = {
    ...style,
    ...sharedStyle,
    ...(color !== "currentColor" ? { color } : {}),
    userSelect: "none",
    WebkitUserSelect: "none",
  };
  const glyphStyle: CSSProperties = {
    fontFamily: '"Material Symbols Rounded"',
    fontSize: scaleIconSize(computedSize, MATERIAL_SYMBOLS_SCALE),
    fontVariationSettings: `'FILL' ${iconDefinition.fill}, 'wght' 400, 'GRAD' 0, 'opsz' 20`,
    fontVariantLigatures: "normal",
    fontFeatureSettings: '"liga"',
    WebkitFontFeatureSettings: '"liga"',
    WebkitFontSmoothing: "antialiased",
  };

  return (
    <span
      style={containerStyle}
      className={twMerge(MATERIAL_SYMBOLS_BASE_CLASSNAME, className)}
      onClick={onClick}
      {...accessibilityProps}
      {...props}
    >
      <span
        aria-hidden={true}
        style={glyphStyle}
        className={MATERIAL_SYMBOLS_GLYPH_CLASSNAME}
      >
        {iconDefinition.ligature}
      </span>
    </span>
  );
};

const renderSvgIcon = ({
  accessibilityProps,
  className,
  color,
  description,
  iconDefinition,
  manualViewBox,
  onClick,
  props,
  sharedStyle,
  style,
}: {
  accessibilityProps: AccessibilityProps;
  className: IIcon["className"];
  color: NonNullable<IIcon["color"]>;
  description: IIcon["description"];
  iconDefinition: SvgIconComponentType;
  manualViewBox: IIcon["viewBox"];
  onClick: IIcon["onClick"];
  props: IconChildrenProps;
  sharedStyle: CSSProperties;
  style: CSSProperties;
}) => {
  const IconComponent = iconDefinition;
  const svgStyle: CSSProperties = {
    ...style,
    ...sharedStyle,
    fill: color,
  };

  return (
    <IconComponent
      style={svgStyle}
      className={twMerge("shrink-0 select-none", className)}
      onClick={onClick}
      desc={description ?? undefined}
      {...accessibilityProps}
      {...(manualViewBox ? { viewBox: manualViewBox } : {})}
      {...props}
    />
  );
};

const isMaterialFontIconDefinition = (
  iconDefinition: IconDefinition,
): iconDefinition is MaterialFontIconDefinition =>
  typeof iconDefinition === "object" &&
  iconDefinition !== null &&
  "kind" in iconDefinition &&
  iconDefinition.kind === "font";

const materialSymbolsFontFaceId = "sellar-material-symbols-rounded-font-face";

const scaleIconSize = (
  size: NonNullable<IIcon["size"]>,
  scale: number,
): NonNullable<IIcon["size"]> =>
  typeof size === "number" ? size * scale : `calc(${size} * ${scale})`;

const ensureMaterialSymbolsFontFace = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(materialSymbolsFontFaceId)) return;

  const styleElement = document.createElement("style");
  styleElement.id = materialSymbolsFontFaceId;
  styleElement.textContent = `@font-face {
  font-family: "Material Symbols Rounded";
  font-style: normal;
  font-weight: 400;
  font-display: block;
  src: url("${materialSymbolsRoundedSubsetUrl}") format("woff2");
}`;

  document.head.appendChild(styleElement);
};

const useMaterialGlyphSupport = (
  iconDefinition: MaterialFontIconDefinition | null,
) => {
  const cacheKey = iconDefinition
    ? `${iconDefinition.ligature}:${iconDefinition.fill}`
    : null;
  const cachedValue =
    cacheKey !== null ? materialGlyphSupportCache.get(cacheKey) : undefined;

  // Optimistic "supported" until async measurement resolves (or cache hits).
  const [measuredSupport, setMeasuredSupport] = useState<boolean | null>(null);
  const [measuredKey, setMeasuredKey] = useState<string | null>(null);

  if (measuredKey !== cacheKey) {
    setMeasuredKey(cacheKey);
    setMeasuredSupport(null);
  }

  useLayoutEffect(() => {
    if (!iconDefinition || !cacheKey || typeof document === "undefined") {
      return;
    }

    if (materialGlyphSupportCache.has(cacheKey)) {
      return;
    }

    let cancelled = false;
    const updateSupport = () => {
      const supported = hasRenderableMaterialGlyph(iconDefinition);
      materialGlyphSupportCache.set(cacheKey, supported);

      if (!cancelled) {
        setMeasuredSupport(supported);
      }
    };

    if (!document.fonts?.load) {
      return () => {
        cancelled = true;
      };
    }

    document.fonts
      .load(
        `${MATERIAL_SYMBOLS_MEASUREMENT_FONT_SIZE_PX}px "Material Symbols Rounded"`,
        iconDefinition.ligature,
      )
      .then(() => {
        if (!cancelled) {
          updateSupport();
        }
      })
      .catch(() => {
        // Keep optimistic supported=true on load failure.
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, iconDefinition]);

  if (!iconDefinition || !cacheKey || typeof document === "undefined") {
    return true;
  }

  if (cachedValue !== undefined) {
    return cachedValue;
  }

  if (measuredSupport !== null) {
    return measuredSupport;
  }

  return true;
};

const hasRenderableMaterialGlyph = (
  iconDefinition: MaterialFontIconDefinition,
) => {
  if (typeof document === "undefined") return true;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) return true;

  context.font = `400 ${MATERIAL_SYMBOLS_MEASUREMENT_FONT_SIZE_PX}px "Material Symbols Rounded"`;

  // Material Symbols ligatures should resolve to a single icon-width glyph.
  // If the browser falls back to plain text, the measured width is far larger.
  const measuredWidth = context.measureText(iconDefinition.ligature).width;

  return measuredWidth <= MATERIAL_SYMBOLS_MEASUREMENT_FONT_SIZE_PX * 1.5;
};
